import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { generateSyllabus, saveStudyPlan, fetchDashboard } from "../api";

export default function SyllabusPlanner() {
  const [examName, setExamName] = useState("");
  const [timeframe, setTimeframe] = useState("6 months");
  const [syllabus, setSyllabus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();


  useEffect(() => {
    fetchDashboard().then(data => {
      if (data && data.studyPlan) {
        setSyllabus(data.studyPlan);
        setExamName(data.studyPlan.exam);
        setTimeframe(data.studyPlan.timeframe);
      }
      setFetching(false);
    }).catch(() => setFetching(false));
  }, []);
  const handleGenerate = async (e) => {
    if (e) e.preventDefault();
    if (!examName.trim()) return;

    setLoading(true);
    setSyllabus(null);
    try {
      const data = await generateSyllabus(examName, timeframe);
      setSyllabus(data);
    } catch (err) {
      alert("Error generating syllabus: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await saveStudyPlan(syllabus);
      alert("Your study path has been locked in!");
      navigate("/");
    } catch (err) {
      alert("Error saving study plan: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const startTopicPractice = (topic) => {
    // Navigate to practice session with this topic
    navigate(`/practice/session?topic=${encodeURIComponent(topic)}&mode=ai`);
  };

  if (fetching) return <div className="loading"><div className="spinner" /><span>Loading your plan…</span></div>;

  return (
    <div className="fade-in">
      <div style={{ marginBottom: 32 }}>
        <h1 className="page-title">AI Study Planner</h1>
        <p className="page-sub">Tell AI your goal, and we'll build your roadmap to success.</p>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <form onSubmit={handleGenerate} style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 250 }}>
            <label className="form-label">Which exam are you preparing for?</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. SBI PO, IBPS Clerk, SSC CGL..."
              value={examName}
              onChange={(e) => setExamName(e.target.value)}
              required
            />
          </div>
          <div style={{ width: 180 }}>
            <label className="form-label">Available Time</label>
            <select
              className="form-input"
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
            >
              <option>3 months</option>
              <option>6 months</option>
              <option>8 months</option>
              <option>12 months</option>
            </select>
          </div>
          <button className="btn btn-blue" disabled={loading} style={{ height: 42 }}>
            {loading ? "Generating..." : "Create My Path"}
          </button>
        </form>
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <div className="spinner" style={{ margin: "0 auto 16px" }}></div>
          <p style={{ color: "var(--text-muted)" }}>Our AI is analyzing exam patterns and structuring your syllabus...</p>
        </div>
      )}

      {syllabus && (
        <div className="fade-in">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700 }}>Your Roadmap for {syllabus.exam}</h2>
            <button className="btn btn-green" onClick={handleConfirm} disabled={saving}>
              {saving ? "Saving..." : "✅ Yes, I'm ready to Start"}
            </button>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {syllabus.phases.map((phase, idx) => (
              <div key={idx} className="card" style={{ borderLeft: "4px solid var(--blue)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "var(--blue)" }}>{phase.phaseName}</h3>
                  <span className="badge badge-blue">{phase.duration}</span>
                </div>
                
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
                  {phase.topics.map((t, tIdx) => (
                    <div 
                      key={tIdx} 
                      className="topic-item-ai"
                      onClick={() => startTopicPractice(t.topic)}
                      style={{ 
                        padding: 12, borderRadius: 8, background: "#f8f9fa", border: "1px solid #eee", 
                        cursor: "pointer", transition: "all 0.2s" 
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "var(--blue)", textTransform: "uppercase" }}>{t.subject}</span>
                        <span style={{ fontSize: 10, color: "var(--text-muted)" }}>Practice →</span>
                      </div>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{t.topic}</div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: "1.4" }}>{t.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
